import { countryCodes } from '@va/assets';
import { updateWebsite } from '@va/dashboard/actions/api';
import { useUpdateWebsiteSettings } from '@va/dashboard/api-client/Website';
import { getWebsite } from '@va/dashboard/selectors/core';
import { SettingsHeading } from '@va/dashboard/ui/pages';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { LocationIcon, PieChartHighlightIcon, Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import { IconWithBackground, SelectDropdown, SelectDropdownOption } from '@va/ui/design-system';
import { Flag } from '@va/util/components';
import { useBusinessSectors } from '@va/util/hooks';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const WebsiteRegionAndBusinessSectorCard = () => {
  const translate = useTranslate();
  const isContributor = useSelector(contributorRole);

  const website = useSelector(getWebsite);
  const dispatch = useDispatch();
  const { showErrorNotification } = useAddNotification();

  const countryOptions = useMemo(() => {
    return countryCodes.map((item) => {
      return {
        label: translate(`country.name.${item.alpha2}`),
        value: item.alpha2,
        icon: <Flag countryCode={item.alpha2} className='rounded-full h-15px w-15px' size='1x1' />,
      } as SelectDropdownOption<string>;
    });
  }, [translate]);

  const updateCountry = useUpdateWebsiteSettings();
  const updateBusinessSector = useUpdateWebsiteSettings();

  const selectedCountryOption = useMemo(
    () => countryOptions.find((item) => item.value === website.country),
    [countryOptions, website.country],
  );

  const { businessSectorOptions, getSelectedBusinessSector } = useBusinessSectors();
  const selectedBusinessSector = useMemo(
    () => getSelectedBusinessSector(website.businessSector),
    [getSelectedBusinessSector, website.businessSector],
  );

  return (
    <div className='flex flex-col gap-6u items-start'>
      <SettingsHeading
        title={translate('card.websiteRegionAndBusinessSector.title')}
        subText={translate('card.websiteRegionAndBusinessSector.subtitle')}
      />
      <div className='flex flex-col w-full gap-6u'>
        <SelectDropdown
          icon={
            <IconWithBackground
              className='bg-white!'
              icon={() => {
                return updateCountry.isLoading ? (
                  <Spinner className='h-4 w-4' />
                ) : website.country ? (
                  <Flag countryCode={website.country} className='rounded-full h-4 w-4' size='1x1' />
                ) : (
                  <LocationIcon className='h-4 w-4' />
                );
              }}
            />
          }
          disabled={isContributor}
          disabledTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
          label={translate('card.websiteRegionAndBusinessSector.websiteRegion.label')}
          onChange={async (option) => {
            try {
              await updateCountry.execute({ country: option.value });
              dispatch(updateWebsite({ country: option.value }));
            } catch (error) {
              showErrorNotification();
              updateCountry.reset();
            }
          }}
          selectedOption={selectedCountryOption}
          dropdownOptions={countryOptions}
          dropdownPlacement='bottom-start'
        />

        <SelectDropdown
          icon={
            <IconWithBackground
              className='bg-white!'
              icon={() => {
                return updateBusinessSector.isLoading ? (
                  <Spinner className='h-4 w-4' />
                ) : (
                  <PieChartHighlightIcon color='#5C5C5C' className='h-4 w-4' />
                );
              }}
            />
          }
          disabled={isContributor}
          disabledTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
          label={translate('card.websiteRegionAndBusinessSector.businessSector.label')}
          onChange={async (option) => {
            try {
              await updateBusinessSector.execute({ businessSector: option.value });
              dispatch(updateWebsite({ businessSector: option.value }));
            } catch (error) {
              showErrorNotification();
              updateBusinessSector.reset();
            }
          }}
          selectedOption={selectedBusinessSector}
          dropdownOptions={businessSectorOptions}
          dropdownPlacement='bottom-start'
        />
      </div>
    </div>
  );
};
